import React, { useState, useEffect } from 'react';

const DEFAULTS = {
  enabled: true,
  sites: [],
  block_method: 'layer7',
};

const POPULAR_SITES = [
  { domain: 'facebook.com', icon: '📘', name: 'Facebook' },
  { domain: 'tiktok.com', icon: '🎵', name: 'TikTok' },
  { domain: 'youtube.com', icon: '▶️', name: 'YouTube' },
  { domain: 'twitter.com', icon: '🐦', name: 'Twitter/X' },
  { domain: 'instagram.com', icon: '📷', name: 'Instagram' },
  { domain: 'shopee.co.th', icon: '🛒', name: 'Shopee' },
  { domain: 'lazada.co.th', icon: '🛍️', name: 'Lazada' },
  { domain: 'netflix.com', icon: '🎬', name: 'Netflix' },
];

export default function StepWebsiteBlock({ data, onChange, onNext, onBack }) {
  const [form, setForm] = useState(data || DEFAULTS);
  const [newSite, setNewSite] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const update = (key, value) => {
    const updated = { ...form, [key]: value };
    setForm(updated);
    onChange(updated);
  };

  const addSite = () => {
    const domain = newSite.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    if (!domain) { setErrorMsg('กรุณากรอกชื่อเว็บไซต์'); return; }
    if ((form.sites || []).includes(domain)) { setErrorMsg('เว็บไซต์นี้มีอยู่แล้ว'); return; }
    setErrorMsg('');
    update('sites', [...(form.sites || []), domain]);
    setNewSite('');
  };

  const removeSite = (index) => {
    update('sites', form.sites.filter((_, i) => i !== index));
  };

  const addPopular = (domain) => {
    if ((form.sites || []).includes(domain)) return;
    update('sites', [...(form.sites || []), domain]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addSite(); }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">🚫 บล็อกเว็บไซต์</h2>
        <p className="text-gray-500 dark:text-gray-400">บล็อกการเข้าถึงเว็บไซต์ที่ไม่ต้องการในเครือข่าย</p>
      </div>

      {/* Enable */}
      <label className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 cursor-pointer mb-4">
        <div>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">เปิดใช้งานการบล็อกเว็บไซต์</span>
          <p className="text-xs text-gray-400 mt-0.5">บล็อกเว็บไซต์ตามรายการที่กำหนด</p>
        </div>
        <input type="checkbox" checked={form.enabled} onChange={e => update('enabled', e.target.checked)} className="w-5 h-5 text-red-600 rounded" />
      </label>

      {form.enabled && (
        <>
          {/* Popular Sites */}
          <div className="mb-4 p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border-2 border-gray-200 dark:border-gray-600 shadow-sm">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3">📋 เว็บไซต์ยอดนิยม (คลิกเพื่อเพิ่มทันที)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {POPULAR_SITES.map(site => {
                const isBlocked = (form.sites || []).includes(site.domain);
                return (
                  <button
                    key={site.domain}
                    onClick={() => isBlocked ? removeSite(form.sites.indexOf(site.domain)) : addPopular(site.domain)}
                    className={`p-3 rounded-xl border-2 text-left text-sm transition-all ${
                      isBlocked
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <div className="text-lg mb-0.5">{site.icon}</div>
                    <div className="font-bold text-xs">{site.name}</div>
                    <div className="text-[10px] opacity-70">{site.domain}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Add Custom Site */}
          <div className="mb-4 p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border-2 border-gray-200 dark:border-gray-600 shadow-sm">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3">➕ เพิ่มเว็บไซต์เอง</h3>
            {errorMsg && (
              <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-600 dark:text-red-400 animate-fadeIn">
                ⚠️ {errorMsg}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={newSite}
                onChange={e => { setNewSite(e.target.value); setErrorMsg(''); }}
                onKeyDown={handleKeyDown}
                className="input-field text-sm flex-1"
                placeholder="เช่น google.com, pantip.com"
              />
              <button onClick={addSite} className="btn-primary text-sm whitespace-nowrap">เพิ่ม</button>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">💡 กรอกเฉพาะชื่อโดเมน ไม่ต้องมี http:// หรือ www.</p>
          </div>

          {/* Blocked Sites List */}
          {form.sites && form.sites.length > 0 && (
            <div className="mb-4 p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border-2 border-gray-200 dark:border-gray-600 shadow-sm">
              <h3 className="font-bold text-gray-900 dark:text-white mb-3">🚫 เว็บไซต์ที่ถูกบล็อก ({form.sites.length} รายการ)</h3>
              <div className="space-y-2">
                {form.sites.map((site, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800/50">
                    <div className="flex items-center gap-2">
                      <span className="text-red-500">🚫</span>
                      <span className="text-sm font-mono text-gray-700 dark:text-gray-300">{site}</span>
                    </div>
                    <button onClick={() => removeSite(i)} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 rounded-lg">
            <p className="text-xs text-red-700 dark:text-red-300">
              <strong>💡 วิธีทำงาน:</strong> ใช้ Layer 7 Protocol ตรวจจับชื่อโดเมนใน HTTP Request และบล็อกการเชื่อมต่อ
            </p>
          </div>
        </>
      )}

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary">← ย้อนกลับ</button>
        <button onClick={onNext} className="btn-primary">ถัดไป ({(form.sites || []).length} เว็บไซต์)</button>
      </div>
    </div>
  );
}
